<template>
  <div class="row sba-open-frame">
    <div class="col s12 sba-open-frame-col">
      <LoadingDiv v-if="!isReady" />

      <div class="finance-invoices sba-open-workbench" v-else>
        <div id="finance-invoices-top"></div>

        <div class="invoice-review-head">
          <div class="sba-open-title">
            <label class="active teal-text">Finance Invoices</label>
            <h5>Invoice Approval</h5>
            <p>Search, inspect, and approve premium invoices that have moved into finance review.</p>
          </div>

          <div class="invoice-head-filter" v-show="showSearch">
            <FilterInvoice compact-client-search @search="handleSearch" />
          </div>

          <span class="invoice-count sba-open-count" v-show="!showSearch">
            {{ selectedInvoiceCount }}
            <small>{{ selectedInvoiceCount === 1 ? "invoice" : "invoices" }}</small>
          </span>
        </div>

        <div class="progress invoice-progress" v-if="isPageLoading">
          <div class="indeterminate"></div>
        </div>

        <!-- Invoices -->
        <div class="invoice-list-card">
          <!-- Start -->
          <div v-for="(invoice, index) in invoices" :key="invoice.id">
            <!-- Principal -->
            <div
              class="invoice-row-card"
              :class="{
                'is-open': currInvoice?.id === invoice?.id,
                'is-overdue':
                  invoice.status == 'p' && invoice.start < currServerDate,
                'is-pending':
                  invoice.status == 'p' && invoice.start >= currServerDate,
                'is-complete': invoice.status == 'c',
                'is-grace': invoice.status == 'z',
                'is-deleted': invoice.status == 'd',
              }"
              v-if="currInvoice?.id === invoice?.id || !currInvoice"
            >
              <div class="row mb-zero">
                <div class="col s1">
                  <span class="invoice-number">{{ numberBadge(index) }}</span>
                </div>
                <div class="col s10">
                  <p class="invoice-status-line">
                    <span
                      v-if="
                        invoice.status == 'p' && invoice.start < currServerDate
                      "
                      >Overdue</span
                    >
                    <span
                      v-if="
                        invoice.status == 'p' && invoice.start >= currServerDate
                      "
                      >Pending</span
                    >
                    <span
                      class="orange-text text-darken-4"
                      v-if="invoice.status == 'z'"
                      >Unpaid (Grace Period)</span
                    >
                    <span v-if="invoice.status == 'c'">Completed Payment</span>
                    <span class="red-text" v-if="invoice.status == 'd'"
                      >Deleted</span
                    >
                  </p>
                  <h6 class="invoice-client">
                    {{ invoiceClientName(invoice) }}
                    <span class="invoice-category">{{
                      invoiceClientCat(invoice)
                    }}</span>
                  </h6>
                  <h6 class="invoice-amount">
                    ₦{{
                      changeToNaira(invoice.input_total, invoice.output_total)
                    }}
                    <span class="invoice-period">
                      <small>
                        {{
                          new Date(invoice.start).toLocaleString(undefined, {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          })
                        }}
                      </small>

                      -

                      <small>
                        {{
                          new Date(invoice.end).toLocaleString(undefined, {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          })
                        }}
                      </small>
                    </span>
                  </h6>
                </div>
                <div class="col s1">
                  <span class="right" v-if="!currInvoice">
                    <button
                      type="button"
                      class="invoice-action right"
                      :class="{
                        'is-late': invoice.start < currServerDate && (invoice.status == 'p' || invoice.status == 'z'),
                        'is-normal': !(
                          invoice.start < currServerDate && (invoice.status == 'p' || invoice.status == 'z')
                        ),
                      }"
                      @click="handleView(invoice)"
                      title="view details"
                    >
                      <i class="material-icons">chevron_right</i>
                      <span class="invoice-action-label">Details</span>
                    </button>
                  </span>
                  <span class="right" v-else>
                    <button type="button" class="invoice-action is-close right" @click="handleClear()" title="go back">
                      <i class="material-icons">keyboard_return</i>
                      <span class="invoice-action-label">Back</span>
                    </button>
                  </span>
                </div>
              </div>
            </div>

            <div
              class="finance-approval-card"
              v-if="
                currInvoice?.id === invoice?.id &&
                (invoice.status == 'p' || invoice.status == 'z')
              "
            >
              <button
                type="button"
                class="finance-invoice-action is-secondary"
                :disabled="isSubmitting"
                v-if="invoice.status == 'p'"
                @click.prevent="handleSubmit('later')"
              >
                Pay Later
              </button>
              <button
                type="button"
                class="finance-invoice-action is-primary"
                :disabled="isSubmitting"
                @click.prevent="handleSubmit('paid')"
              >
                Approve as Paid
              </button>
            </div>
          </div>
        </div>

        <!-- Invoice details -->
        <InvoiceDiv
          :currInvoice="currInvoice"
          :seamless="true"
          scope="billable"
          v-if="currInvoice"
        />

        <!-- Pages -->
        <template v-else>
          <div class="invoice-empty-state" v-if="!invoices.length">
            <div class="invoice-empty-icon">
              <i class="material-icons">receipt_long</i>
            </div>
            <h6>{{ emptyInvoiceTitle }}</h6>
            <p>{{ emptyInvoiceDescription }}</p>
          </div>

          <PaginationDiv :payLoad="allPages" @newPage="handlePage" v-else />
        </template>
      </div>
    </div>
  </div>
</template>

  <script>
import { computed, onMounted, ref, watch } from "vue";
import { changeToMoney } from "@/composables/fxLibrary/coreFx";
import getItems from "@/composables/getItems";
import postItem from "@/composables/postItem";
import InvoiceDiv from "@/components/invoice/InvoiceDiv.vue";
import FilterInvoice from "@/components/invoice/FilterInvoiceDiv.vue";
import PaginationDiv from "@/components/pager/PaginationDiv.vue";
import LoadingDiv from "@/components/LoadingDiv.vue";
import nativeUi from "@/ui/native-ui";

export default {
  name: "InvoicesFinanceComponent",
  components: { LoadingDiv, PaginationDiv, InvoiceDiv, FilterInvoice },
  props: ["resetKey"],
  setup(props) {
    const response = ref(null);
    const isReady = ref(false);
    const isLoading = ref(false);
    const isPageLoading = ref(false);
    const isSubmitting = ref(false);
    const error = ref(null);
    const currInvoice = ref(false);
    const currUrl = ref(null);
    const currHook = ref(null);
    const currServerDate = ref(null);
    const showSearch = ref(true);
    const searchItem = ref(null);
    const searchTitle = ref(null);
    const filterState = ref({
      category: "",
      categoryLabel: "Open Invoices",
      client: "",
      clientLabel: "",
    });

    // get function
    const getItemsFromApi = (table) => {
      (async () => {
        const { res, err } = await getItems(table);

        if (res.value) {
          response.value = res.value.data;
          currServerDate.value = res.value.server;
        } else {
          error.value = err.value;
        }

        // page is ready
        isReady.value = true;
        isLoading.value = false;
        isPageLoading.value = false;
      })();
    };

    // to naira
    const changeToNaira = (i, o) => {
      // sort value
      let val = i - o;
      // conclude
      const { fx } = changeToMoney(val);
      return fx.value;
    };

    const invoiceClient = (invoice) => {
      return invoice?.policy?.client || invoice?.client || {};
    };

    const invoiceClientName = (invoice) => {
      return invoiceClient(invoice)?.name || "Client";
    };

    const invoiceClientCat = (invoice) => {
      return invoiceClient(invoice)?.cat?.toUpperCase() || "";
    };

    const numberBadge = (num) => {
      return (
        (allPages.value.current_page - 1) * allPages.value.per_page + num + 1
      );
    };

    // starter
    onMounted(() => {
      // loader
      isReady.value = false;
      // start from the top
      getInvoices();
    });

    // invoices
    const getInvoices = () => {
      // set current Url
      currUrl.value = "group/invoices/group";
      // set current Hook
      currHook.value = "?";
      // get items from function
      getItemsFromApi(currUrl.value);
      // clear
      handleClear();
    };

    // handle search (emitted from component)
    const handleSearch = (val) => {
      // start loading
      isLoading.value = true;
      // set current Url
      currUrl.value = val.currUrl;
      // set current Hook
      currHook.value = val.currHook;
      // set search title
      searchTitle.value = val.searchTitle;
      // set current filter state
      filterState.value = val.filterState || filterState.value;
      // get items from function
      getItemsFromApi(currUrl.value);
      // clear
      handleClear();
    };

    // switch page
    const handlePage = (page) => {
      // loader
      isLoading.value = true;
      isPageLoading.value = true;
      scrollToInvoiceTop();
      // get new page items from function
      getItemsFromApi(`${currUrl.value}${currHook.value}page=${page}`);
      // clear
      handleClear();
    };

    const scrollToInvoiceTop = () => {
      requestAnimationFrame(() => {
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      });
    };

    // enrollee ction
    const handleClear = () => {
      currInvoice.value = null;
      showSearch.value = true;
    };

    watch(
      () => props.resetKey,
      () => {
        handleClear();
      }
    );

    // enrollee ction
    const handleView = (val) => {
      currInvoice.value = val;
      showSearch.value = false;
      scrollToInvoiceTop();
    };

    const handleSubmit = async (val) => {
      isSubmitting.value = true;

      const newStatus = val === "paid" ? "c" : val === "later" ? "z" : null;

      if (!newStatus) {
        isSubmitting.value = false;
        return;
      }

      const { res, err } = await postItem(
        "operator/finance/premium/payment/confirmation",
        {
          group_id: currInvoice.value.id,
          new_status: newStatus,
        }
      );

      if (res.value) {
        nativeUi.toast({
          html: "Invoice updated",
          classes: "green",
          displayLength: 5000,
          inDuration: 300,
          outDuration: 300,
        });
        getInvoices();
      } else {
        error.value = err.value;
      }

      isSubmitting.value = false;
      handleClear();
    };

    // output results
    const invoices = computed(() => {
      if (!response.value) {
        return [];
      } else {
        return response.value.data;
      }
    });

    // output pages
    const allPages = computed(() => {
      if (!response.value) {
        return [];
      } else {
        return response.value; // get all
      }
    });

    const selectedInvoiceCount = computed(() => {
      return Number(
        currInvoice.value?.billable_invoices_count ||
          currInvoice.value?.invoices_count ||
          0
      );
    });

    const emptyInvoiceTitle = computed(() => {
      const categoryLabel = filterState.value.categoryLabel || "Open Invoices";
      const clientLabel = filterState.value.clientLabel;

      if (filterState.value.category === "all" && !clientLabel) {
        return "No invoices found";
      }

      if (clientLabel) {
        return `No invoices found for ${clientLabel}`;
      }

      return `No ${categoryLabel.toLowerCase()} invoices found`;
    });

    const emptyInvoiceDescription = computed(() => {
      const categoryLabel = filterState.value.categoryLabel || "Open Invoices";
      const clientLabel = filterState.value.clientLabel;

      if (filterState.value.category === "all" && !clientLabel) {
        return "There are no invoice records matching the current finance view.";
      }

      if (clientLabel && filterState.value.category !== "all") {
        return `There are no ${categoryLabel.toLowerCase()} invoice records for this client.`;
      }

      if (clientLabel) {
        return "There are no invoice records for this client.";
      }

      return `There are no invoice records under the ${categoryLabel} filter.`;
    });

    return {
      searchTitle,
      handleSearch,
      handleSubmit,
      handlePage,
      handleClear,
      handleView,
      numberBadge,
      changeToNaira,
      invoiceClientName,
      invoiceClientCat,
      currServerDate,
      currInvoice,
      showSearch,
      searchItem,
      isReady,
      isLoading,
      isPageLoading,
      isSubmitting,
      error,
      invoices,
      allPages,
      selectedInvoiceCount,
      emptyInvoiceTitle,
      emptyInvoiceDescription,
    };
  },
};
</script>

    <style scoped>
.finance-invoices {
  color: #263238;
  width: 100%;
}

.sba-open-frame {
  margin-left: 0;
  margin-right: 0;
}

.sba-open-frame-col {
  padding-left: 0 !important;
  padding-right: 0 !important;
}

.invoice-review-head,
.invoice-filter-card,
.invoice-list-card,
.invoice-row-card {
  background: #ffffff;
  border: 1px solid #eeeeee;
  border-radius: 6px;
  box-shadow: none;
}

.invoice-review-head {
  align-items: end;
  background: transparent;
  border: 0;
  border-bottom: 1px solid var(--ui-border);
  border-radius: 0;
  box-shadow: none;
  display: grid;
  gap: 2rem;
  grid-template-columns: minmax(18rem, 1fr) minmax(30rem, 44rem);
  margin: 0 0 1.5rem;
  padding: 0 0 1.25rem;
}

.invoice-review-head h5 {
  color: var(--ui-foreground);
  font-family: var(--ui-font-sans);
  font-size: 1.25rem;
  font-weight: 600;
  letter-spacing: -0.02em;
  line-height: 1.35;
  margin: 0;
}

.invoice-review-head p {
  color: var(--ui-muted-foreground);
  font-family: var(--ui-font-sans);
  font-size: 0.875rem;
  line-height: 1.5;
  margin: 0.35rem 0 0;
  max-width: 42rem;
}

.invoice-count {
  align-items: flex-end;
  background: #fafafa;
  border: 1px solid #eeeeee;
  border-radius: 6px;
  color: #263238;
  display: inline-flex;
  flex-direction: column;
  font-size: 1.18rem;
  font-weight: 700;
  line-height: 1.1;
  min-width: 86px;
  padding: 10px 12px;
}

.invoice-count small {
  color: #455a64;
  font-size: 0.75rem;
  font-weight: 600;
}

.invoice-head-filter {
  background: transparent;
  border: 0;
  border-radius: 0;
  box-shadow: none;
  min-width: 0;
  padding: 0;
  width: 100%;
}

.invoice-head-filter :deep(.section) {
  margin: 0;
  padding: 0;
}

.invoice-head-filter :deep(.row) {
  align-items: end;
  display: grid;
  gap: 0.75rem;
  grid-template-columns: minmax(11rem, 0.8fr) minmax(15rem, 1.2fr);
  margin-left: 0;
  margin-right: 0;
  margin-bottom: 0;
}

.invoice-head-filter :deep(.col) {
  min-width: 0;
  padding-left: 0 !important;
  padding-right: 0 !important;
  width: 100% !important;
}

.invoice-head-filter :deep(.col + .col) {
  border-left: 0;
}

.invoice-head-filter :deep(.input-field) {
  height: 2.75rem;
  margin-bottom: 0;
  margin-top: 0;
  position: relative;
}

.invoice-head-filter :deep(nav) {
  background: var(--ui-background) !important;
  border: 1px solid var(--ui-border);
  border-radius: calc(var(--ui-radius) - 2px);
  box-shadow: none !important;
  height: 2.75rem;
  line-height: 2.75rem;
  overflow: visible;
}

.invoice-head-filter :deep(.nav-wrapper) {
  background: transparent;
  height: 2.75rem;
  line-height: 2.75rem;
}

.invoice-head-filter :deep(input),
.invoice-head-filter :deep(.select-wrapper input.select-dropdown) {
  background: transparent;
  border: 0 !important;
  border-radius: 0;
  box-sizing: border-box;
  color: var(--ui-foreground) !important;
  font-family: var(--ui-font-sans);
  font-size: 0.875rem;
  font-weight: 500;
  height: calc(2.75rem - 2px) !important;
  line-height: calc(2.75rem - 2px);
  margin: 0;
  padding: 0 2.75rem 0 0.875rem !important;
  width: 100%;
}

.invoice-head-filter :deep(input:focus) {
  box-shadow: none !important;
}

.invoice-head-filter :deep(.material-icons) {
  color: var(--ui-muted-foreground) !important;
  font-size: 1.1rem;
  height: 2.75rem;
  line-height: 2.75rem;
  position: absolute;
  right: 0.75rem;
  top: 0;
}

.invoice-head-filter :deep(label) {
  color: #607d8b !important;
}

.invoice-filter-card {
  background: #fafafa;
  padding: 16px;
}

.invoice-list-card {
  background: transparent;
  border: 0;
  border-radius: 0;
  display: grid;
  gap: 0;
  margin-top: 0;
}

.invoice-row-card {
  align-items: stretch;
  background: transparent;
  border: 0;
  border-bottom: 1px solid var(--ui-border);
  border-radius: 0;
  border-top: 1px solid #eeeeee;
  box-shadow: none;
  padding: 1rem 0;
  position: relative;
  transition: background-color 0.18s ease, border-color 0.18s ease;
}

.invoice-row-card > .row {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 0;
}

.invoice-row-card:is(:hover, .is-open) {
  background: color-mix(in srgb, var(--ui-muted) 42%, transparent);
  border-bottom-color: var(--ui-border);
  border-top-color: transparent;
}

.invoice-list-card > div:first-child .invoice-row-card {
  border-top: 0;
  border-top-left-radius: 0;
  border-top-right-radius: 0;
  box-shadow: none;
}

.invoice-row-card.is-pending {
  border-left-color: #eeeeee;
}

.invoice-row-card.is-complete {
  border-left-color: #eeeeee;
}

.invoice-row-card.is-grace {
  border-left-color: #eeeeee;
}

.invoice-row-card.is-deleted {
  border-left-color: #eeeeee;
}

.invoice-number {
  align-items: center;
  background: transparent;
  border-radius: 0;
  color: #607d8b;
  display: inline-flex;
  font-size: 0.92rem;
  font-weight: 700;
  height: auto;
  justify-content: center;
  width: auto;
}

@media only screen and (min-width: 993px) {
  .invoice-row-card > .row > .col:first-child {
    padding-left: 1.75rem !important;
  }
}

.invoice-status-line {
  align-items: center;
  background: transparent;
  border: 1px solid var(--ui-border);
  border-radius: 999px;
  color: var(--ui-muted-foreground);
  display: flex;
  font-size: 0.75rem;
  font-weight: 600;
  gap: 0.25rem;
  justify-self: start;
  margin: 0;
  padding: 0.25rem 0.55rem;
  width: max-content;
}

.invoice-row-card.is-overdue .invoice-status-line {
  background: color-mix(in srgb, var(--ui-destructive) 5%, var(--ui-background));
  border-color: color-mix(in srgb, var(--ui-destructive) 20%, var(--ui-border));
  color: color-mix(in srgb, var(--ui-destructive) 78%, var(--ui-foreground));
}

.invoice-row-card.is-pending .invoice-status-line {
  background: #fde68a;
  border-color: #eab308;
  color: #713f12;
}

.invoice-row-card.is-grace .invoice-status-line {
  background: color-mix(in srgb, var(--ui-warning) 7%, var(--ui-background));
  border-color: color-mix(in srgb, var(--ui-warning) 22%, var(--ui-border));
  color: color-mix(in srgb, var(--ui-warning) 72%, var(--ui-foreground));
}

.invoice-row-card.is-complete .invoice-status-line,
.invoice-row-card.is-deleted .invoice-status-line {
  background: color-mix(in srgb, var(--ui-success) 6%, var(--ui-background));
  border-color: color-mix(in srgb, var(--ui-success) 20%, var(--ui-border));
  color: var(--ui-success);
}

.invoice-row-card.is-deleted .invoice-status-line {
  background: var(--ui-muted);
  border-color: var(--ui-border);
  color: var(--ui-muted-foreground);
}

.invoice-status-line :deep(.red-text),
.invoice-status-line :deep(.orange-text) {
  color: inherit !important;
}

.invoice-client {
  align-items: center;
  color: #263238;
  display: flex;
  flex-wrap: wrap;
  font-size: 0.875rem;
  font-weight: 600;
  gap: 8px;
  margin: 0;
}

.invoice-category {
  background: transparent;
  border: 1px solid var(--ui-border);
  border-radius: 4px;
  color: #607d8b;
  font-size: 0.6875rem;
  font-weight: 600;
  padding: 0.15rem 0.4rem;
}

.invoice-amount {
  background: transparent;
  border: 0;
  border-radius: 0;
  color: #263238;
  display: inline-flex;
  flex-wrap: wrap;
  font-size: 0.875rem;
  font-weight: 600;
  margin: 0;
  padding: 0;
}

.invoice-period {
  color: #607d8b;
  display: inline-flex;
  font-size: 0.75rem;
  font-weight: 500;
  gap: 5px;
  margin-left: 12px;
}

.invoice-row-card .col.s10 {
  align-items: center;
  display: grid;
  gap: 10px;
  grid-template-columns: minmax(170px, 0.9fr) minmax(280px, 1.45fr) minmax(300px, 1.15fr);
}

.invoice-action {
  align-items: center;
  background: #ffffff;
  border: 1px solid #eeeeee;
  border-radius: calc(var(--ui-radius) - 2px);
  color: #607d8b;
  cursor: pointer;
  display: inline-flex;
  height: 2.5rem;
  justify-content: center;
  padding: 0;
  width: 2.5rem;
}

.invoice-action.is-normal {
  background: #fafafa;
}

.invoice-action.is-late {
  background: #ffffff;
  border-color: #efdad7;
  color: #8d3f3f;
}

.invoice-action.is-close {
  background: #eceff1;
  color: #263238;
}

.invoice-action i {
  font-size: 21px;
}

.invoice-action-label {
  display: none;
}

.invoice-row-card:is(:hover, .is-open) .invoice-action {
  background: var(--ui-foreground);
  border-color: var(--ui-foreground);
  color: var(--ui-background);
}

.finance-approval-card {
  align-items: center;
  border-bottom: 1px solid var(--ui-border);
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  padding: 1rem 0;
}

.finance-invoice-action {
  align-items: center;
  border: 1px solid var(--ui-border);
  border-radius: calc(var(--ui-radius) - 2px);
  cursor: pointer;
  display: inline-flex;
  font-family: var(--ui-font-sans);
  font-size: 0.875rem;
  font-weight: 600;
  height: 2.5rem;
  justify-content: center;
  padding: 0 1rem;
}

.finance-invoice-action.is-secondary {
  background: var(--ui-background);
  color: var(--ui-foreground);
}

.finance-invoice-action.is-primary {
  background: var(--ui-foreground);
  border-color: var(--ui-foreground);
  color: var(--ui-background);
}

.finance-invoice-action:hover:not(:disabled) {
  filter: brightness(0.96);
}

.finance-invoice-action:focus-visible {
  outline: 2px solid var(--ui-ring);
  outline-offset: 2px;
}

.finance-invoice-action:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.invoice-empty-state {
  align-items: center;
  background: #ffffff;
  border: 1px solid #eeeeee;
  border-top: 0;
  border-radius: 0 0 6px 6px;
  color: #607d8b;
  display: flex;
  flex-direction: column;
  padding: 38px 18px;
  text-align: center;
}

.invoice-empty-icon {
  align-items: center;
  background: #fafafa;
  border: 1px solid #eeeeee;
  border-radius: 6px;
  color: #607d8b;
  display: inline-flex;
  height: 42px;
  justify-content: center;
  margin-bottom: 12px;
  width: 42px;
}

.invoice-empty-icon i {
  font-size: 22px;
}

.invoice-empty-state h6 {
  color: #263238;
  font-weight: 700;
  margin: 0 0 6px;
}

.invoice-empty-state p {
  margin: 0;
}

.spacer10 {
  margin-top: 10px;
  margin-bottom: 10px;
}

.pointer {
  cursor: default;
}

.edit-item:hover .material-icons.grey-text,
.edit-item:hover .material-icons.grey-text::before {
  color: rgb(21, 21, 206) !important;
}

/* open */
.chip-more {
  cursor: pointer;
  background-color: #eaecee !important;
}

.chip-more:hover {
  color: white !important;
  background-color: black !important;
}

/* close */
.chip-less {
  cursor: pointer;
  color: white !important;
  background-color: black !important;
}

.chip-less:hover {
  color: black !important;
  background-color: #eaecee !important;
}

/* base class */
.tab-custom {
  display: inline-block;
  cursor: pointer;
  margin: 0 0 -1px;
  padding: 10px 15px;
  font-weight: 600;
  text-align: center;
  border: 1px solid transparent;
}

.tab-custom:hover {
  color: #000000 !important;
}

.tab-custom-active {
  display: inline-block;
  cursor: pointer;
  margin: 0 0 -1px;
  padding: 10px 15px;
  color: white;
  font-weight: 600;
  text-align: center;
  background-color: black;
}

/* display */
.tab-custom-section {
  padding: 20px 0 0;
  border-top: 1px solid #ddd;
}

@media only screen and (min-width: 601px) and (max-width: 992px) {
  .invoice-row-card > .row {
    align-items: center;
    display: grid;
    gap: 12px;
    grid-template-columns: 44px minmax(0, 1fr) 54px;
  }

  .invoice-row-card .col {
    padding: 0;
    width: 100% !important;
  }

  .invoice-row-card .col.s10 {
    grid-column: 2;
    grid-template-columns: minmax(150px, 0.9fr) minmax(220px, 1.45fr) minmax(210px, 1.15fr);
  }

  .invoice-row-card .col.s1:last-child {
    grid-column: 3;
  }

  .invoice-action {
    border-radius: 50%;
    box-shadow: 0 8px 18px -18px rgba(38, 50, 56, 0.72);
    height: 44px;
    width: 44px;
  }

  .invoice-action.is-normal,
  .invoice-action.is-late {
    background: #eceff1;
    border-color: #90a4ae;
    color: #263238;
  }

  .invoice-action.is-close {
    background: #cfd8dc;
    border-color: #78909c;
    color: #263238;
  }

  .invoice-action-label {
    display: none;
  }
}

@media only screen and (max-width: 1200px) {
  .invoice-review-head {
    align-items: start;
    grid-template-columns: minmax(0, 1fr);
  }

  .invoice-head-filter {
    max-width: 44rem;
  }
}

@media only screen and (max-width: 600px) {
  .invoice-review-head {
    gap: 1rem;
    padding: 0 0 1.25rem;
  }

  .invoice-review-head h5 {
    font-size: 1.25rem;
    line-height: 1.35;
  }

  .finance-invoices {
    width: 100%;
  }

  .invoice-head-filter {
    flex-basis: auto;
    width: 100%;
  }

  .invoice-head-filter :deep(.row) {
    gap: 0.75rem;
    grid-template-columns: minmax(0, 1fr);
  }

  .invoice-head-filter :deep(.col) {
    border-left: 0 !important;
    width: 100% !important;
  }

  .invoice-head-filter :deep(.col + .col) {
    border-left: 0;
    border-top: 0;
    padding-top: 0 !important;
  }

  .invoice-count {
    align-items: flex-start;
  }

  .invoice-row-card {
    padding: 16px;
  }

  .invoice-row-card > .row {
    align-items: stretch;
    display: grid;
    gap: 10px;
    grid-template-columns: 34px 1fr;
  }

  .invoice-row-card .col {
    margin-bottom: 10px;
    padding: 0;
    width: 100% !important;
  }

  .invoice-row-card .col.s10 {
    grid-column: 2;
    grid-template-columns: 1fr;
  }

  .invoice-row-card .col.s1:last-child {
    display: flex;
    grid-column: 2;
    justify-content: flex-end;
  }

  .invoice-action {
    border-radius: 50%;
    box-shadow: 0 8px 18px -18px rgba(38, 50, 56, 0.72);
    height: 44px;
    width: 44px;
  }

  .invoice-action.is-normal,
  .invoice-action.is-late {
    background: #eceff1;
    border-color: #90a4ae;
    color: #263238;
  }

  .invoice-action.is-close {
    background: #cfd8dc;
    border-color: #78909c;
    color: #263238;
  }

  .invoice-action-label {
    display: none;
  }

  .finance-approval-card {
    align-items: stretch;
    flex-direction: column;
  }
}
</style>

  margin-bottom: 0;
